import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from 'react-query';
import { MeetingAPI } from 'api/meeting/api';
import {
  Meeting,
  MeetingProvider,
  MeetingSettings,
  RecordingMetaExtended,
} from 'api/meeting/types';
import { toastService } from 'components/molecules/toast';
import { useTranslate } from 'hooks/use-translation';

const meetingsDataTransformer = (data: MeetingSettings): MeetingSettings => {
  return {
    ...data,
    recordingMetaByProvider: data.recordingMeta.reduce(
      (config, providerMeta) => ({
        ...config,
        [providerMeta.provider]: {
          ...providerMeta,
          isConnected: providerMeta.cloudRecordingEnabled,
        },
      }),
      {} as Record<MeetingProvider, RecordingMetaExtended>,
    ),
  };
};

export const ALL_MEETINGS_KEY = ['all-meetings'];
export const MEETINGS_SETTINGS_KEY = ['meetings-settings'];
export const useGetAllMeetings = (
  queryOptions: UseQueryOptions<
    Meeting[],
    unknown,
    Meeting[],
    typeof ALL_MEETINGS_KEY
  > = {},
): UseQueryResult<Meeting[]> =>
  useQuery(ALL_MEETINGS_KEY, () => MeetingAPI.getMeetings(), queryOptions);

export const useGetMeetingSettings = (
  queryOptions: UseQueryOptions<
    MeetingSettings,
    unknown,
    MeetingSettings,
    typeof MEETINGS_SETTINGS_KEY
  > = {},
): UseQueryResult<MeetingSettings> =>
  useQuery(MEETINGS_SETTINGS_KEY, () => MeetingAPI.getMeetingSettings(), {
    select: (data) => meetingsDataTransformer(data),
    ...queryOptions,
  });

export const useUpdateMeetingSettings = (
  queryOptions: UseMutationOptions<
    MeetingSettings,
    unknown,
    MeetingSettings,
    { fallback: MeetingSettings | undefined }
  > = {},
) => {
  const queryClient = useQueryClient();
  const translate = useTranslate();

  return useMutation<
    MeetingSettings,
    unknown,
    MeetingSettings,
    { fallback: MeetingSettings | undefined }
  >(
    ['update-meeting-settings'],
    (newSettings) => MeetingAPI.updateMeetingSettings(newSettings),
    {
      onMutate: async (newSettings) => {
        const previousData: MeetingSettings | undefined =
          queryClient.getQueryData(MEETINGS_SETTINGS_KEY);

        queryClient.setQueryData(MEETINGS_SETTINGS_KEY, newSettings);

        return { fallback: previousData };
      },
      onSuccess: () => queryClient.invalidateQueries(MEETINGS_SETTINGS_KEY),
      onError: (err, newData, context) => {
        if (context?.fallback) {
          queryClient.setQueryData(MEETINGS_SETTINGS_KEY, context?.fallback);
        }
        toastService({
          title: translate('api.error.title'),
          message: translate('api.error.message'),
        }).error();
      },
      ...queryOptions,
    },
  );
};
